import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { syncApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants';
import { writeErrorLog } from '@lib/logger/logError';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';
import {
  DEFAULT_FLAGS,
  FEATURE_KEYS,
  type FeaturesDto,
  type OrganizationFeatureKey,
  type FeatureFlagsServerResponse,
} from '@types';

function isObject (x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

function toBool (x: unknown): boolean {
  return x === true;
}

function pickFlagsFromRoot (raw: unknown): FeaturesDto {
  const outEntries = FEATURE_KEYS.map((k) => {
    const v = isObject(raw) ? raw[k] : undefined;
    const value = toBool(v) || DEFAULT_FLAGS[k];
    return [k, value] as const;
  });
  return Object.fromEntries(outEntries) as FeaturesDto;
}

// Module-scoped dedupe tracker: when multiple callers request flags for the
// same org concurrently, they all await the same in-flight request.
let pendingLoad: { orgId: string; promise: Promise<void> } | null = null;

interface FeatureFlagsStore {
  flags: FeaturesDto;
  organizationId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  loadFeatureFlags: (organizationId: string) => Promise<void>;
  clearAndLoad: (organizationId: string) => Promise<void>;
  setFlags: (flags: FeaturesDto) => void;
  resetFlags: () => void;
  updateFlag: (key: OrganizationFeatureKey, value: boolean) => void;
}

export const useFeatureFlagsStore = create<FeatureFlagsStore>()(
  persist(
    (set, get) => ({
      flags: DEFAULT_FLAGS,
      organizationId: null,
      isLoaded: false,
      isLoading: false,
      error: null,

      loadFeatureFlags: async (organizationId: string): Promise<void> => {
        const state = get();

        // Already loaded successfully for this org — skip the network call.
        if (state.isLoaded && state.organizationId === organizationId && state.error === null) {
          return;
        }

        // Same org request already in flight — share the promise.
        if (pendingLoad !== null && pendingLoad.orgId === organizationId) {
          await pendingLoad.promise;
          return;
        }

        set({ isLoading: true, error: null });

        const promise = (async (): Promise<void> => {
          try {
            const url = microServicesRoutes.sync.get.featureFlags.byOrg;
            const { data } = await syncApi.get<FeatureFlagsServerResponse>(
              `${url}/${organizationId}`,
            );

            const normalizedFlags = pickFlagsFromRoot(data);

            set({
              flags: normalizedFlags,
              organizationId,
              isLoaded: true,
              isLoading: false,
              error: null,
            });
          } catch (e) {
            const errorMsg = extractErrorMessage(e);
            void writeErrorLog(
              'Error loading feature flags',
              'FeatureFlagsStore',
              { error: errorMsg, organizationId });

            set({
              flags: DEFAULT_FLAGS,
              organizationId,
              isLoaded: true,
              isLoading: false,
              error: errorMsg,
            });
          }
        })();

        pendingLoad = { orgId: organizationId, promise };

        try {
          await promise;
        } finally {
          if (pendingLoad?.orgId === organizationId) {
            pendingLoad = null;
          }
        }
      },

      clearAndLoad: async (organizationId: string): Promise<void> => {
        pendingLoad = null;
        set({
          flags: DEFAULT_FLAGS,
          organizationId: null,
          isLoaded: false,
          isLoading: false,
          error: null,
        });
        await get().loadFeatureFlags(organizationId);
      },

      setFlags: (flags: FeaturesDto): void => {
        set({ flags, isLoaded: true });
      },

      resetFlags: (): void => {
        pendingLoad = null;
        set({
          flags: DEFAULT_FLAGS,
          organizationId: null,
          isLoaded: false,
          isLoading: false,
          error: null,
        });
      },

      updateFlag: (key: OrganizationFeatureKey, value: boolean): void => {
        const currentFlags = get().flags;
        set({
          flags: { ...currentFlags, [key]: value },
        });
      },
    }),
    {
      name: 'feature-flags-store',
      partialize: (state) => ({
        flags: state.flags,
        organizationId: state.organizationId,
        isLoaded: state.isLoaded,
      }),
    },
  ),
);
